import React, { useEffect, useState, useContext } from "react";
//import { renderCount } from '../../../../appHelpers/appLoggers';
import styled from "styled-components";
import {
  IconButton,
  SelectPicker,
  Message,
  InputNumber,
  useToaster,
  Toggle,
} from "rsuite";
import { useAuth0 } from "@auth0/auth0-react";
import EditIcon from "@rsuite/icons/Edit";
import axiosInstance from "../../../../../appHelpers/axiosInstance";
import { DateTime } from "luxon";
import { UserContext } from "../../_context/UserContext";
import { AlertModal, SeverityAlert, PreviewHeader, Form } from "../../../../ui";

export const EditAlertFC = ({
  dataConfig,
  config,
  dataTable,
  sme,
  refresh,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const toaster = useToaster();
  const initFormSite = {
    user_id: config.user_id,
    system_id: sme,
    field_name: config.field_name,
    operator: config.operator,
    threshold: config.threshold,
    threshold_units: config.threshold_units,
    threshold_offset: config.threshold_offset,
    severity: config.severity,
    alert_id: config.id,
    notify_frequency: config.notify_frequency,
  };

  const fieldSelector = Object.keys(dataTable)
    .filter((key) =>
      [
        "he_level_value",
        "he_level_1_value",
        "he_level_2_value",
        "mag_psia_value",
        "he_pressure_value",
      ].includes(key),
    )
    .map((operator) => ({ label: operator, value: operator }));
  const operatorSelector = [
    "less_than",
    "greater_than",
    "equals",
    "delta_neg",
    "offline",
  ].map((operator) => ({ label: operator, value: operator }));
  const severitySelector = ["high", "medium", "low"].map((operator) => ({
    label: operator,
    value: operator,
  }));
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [open, setOpen] = useState(false);
  const [disable, setDisable] = useState(true);
  const [formValue, setFormValue] = useState(initFormSite);
  const message = (
    <Message showIcon type="success" header="Success">
      You successfully updated an alert to: {sme}
    </Message>
  );

  const { user, userRoles } = useContext(UserContext);
  const [roleDisable, setRoleDisable] = useState(true);

  const handleEnable = () => {
    if (userRoles.includes("superuser") || userRoles.includes("alerter")) {
      setRoleDisable(false);
    }

    if (
      formValue.field_name &&
      formValue.severity &&
      formValue.threshold &&
      formValue.operator
    ) {
      setDisable(false);
    } else {
      setDisable(true);
    }
  };

  const onSubmit = async () => {
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const response = await axiosInstance({
        method: "POST",
        url: `/alert/update/model/`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          sme: sme,
          updated_model: formValue,
        }),
      });
      toaster.push(message, "topCenter");
      refresh();
      handleClose();
    } catch (error) {
      console.error(error);
      toaster.push(errMessage, "topCenter");
    }
  };

  const getAlertPreview = () => {
    if (formValue.operator === "offline") {
      return "System Offline";
    } else if (formValue.operator === "frozen_host") {
      return "Frozen Host Data";
    } else {
      return dataConfig.find((label) => label.pgcolumn === formValue.field_name)
        ? dataConfig.find((label) => label.pgcolumn === formValue.field_name)
            ?.uilabel
        : formValue.field_name;
    }
  };

  const getTriggerDescription = () => {
    if (formValue.field_name && formValue.operator) {
      if (formValue.operator === "offline") {
        return `Trigger When: System is Offline ${formValue.threshold} hour(s).`;
      } else if (config.field_name === "comp_vib_status") {
        return `Trigger When: Compressor is ${formValue.threshold === 1 || formValue.threshold === true ? "ON" : "OFF"}!`;
      } else if (config.operator === "frozen_host") {
        return `Trigger When: System has Frozen data within past ${formValue.threshold} hour(s).`;
      } else {
        return `Trigger When: ${dataConfig.find((label) => label.pgcolumn === formValue.field_name) ? dataConfig.find((label) => label.pgcolumn === formValue.field_name)?.uilabel : formValue.field_name} is ${formValue.operator.replace(/_/g, " ")} · ${formValue.threshold} ${dataConfig.find((label) => label.pgcolumn === formValue.field_name) ? dataConfig.find((label) => label.pgcolumn === formValue.field_name)?.unit : null}`;
      }
    }
    return null;
  };

  const getCurrentValue = () => {
    if (
      formValue.operator === "greater_than" &&
      parseFloat(dataTable[formValue.field_name]) > formValue.threshold
    ) {
      return (
        <SeverityAlert severity={formValue.severity}>
          <b>
            {dataTable[formValue.field_name]}{" "}
            {dataConfig?.find(
              (label) => label.pgcolumn === formValue.field_name,
            )
              ? dataConfig.find(
                  (label) => label.pgcolumn === formValue.field_name,
                )?.unit
              : null}
          </b>
        </SeverityAlert>
      );
    } else if (
      formValue.operator === "less_than" &&
      parseFloat(dataTable[formValue.field_name]) < formValue.threshold
    ) {
      return (
        <SeverityAlert severity={formValue.severity}>
          <b>
            {dataTable[formValue.field_name]}{" "}
            {dataConfig?.find(
              (label) => label.pgcolumn === formValue.field_name,
            )
              ? dataConfig.find(
                  (label) => label.pgcolumn === formValue.field_name,
                )?.unit
              : null}
          </b>
        </SeverityAlert>
      );
    } else if (
      formValue.operator === "equals" &&
      formValue.field_name === "comp_vib_status"
    ) {
      return dataTable[formValue.field_name] ===
        (formValue.threshold.toString() === "true" ? 1 : 0) ? (
        <SeverityAlert severity={formValue.severity}>
          <b>{dataTable[formValue.field_name] === 1 ? "ON" : "OFF"}</b>
        </SeverityAlert>
      ) : (
        <SeverityAlert>
          <b>
            {dataTable[formValue.field_name] === 1 ||
            dataTable[formValue.field_name] === true
              ? "ON"
              : "OFF"}
          </b>
        </SeverityAlert>
      );
    } else if (
      (formValue.operator === "offline" ||
        formValue.operator === "frozen_host") &&
      (formValue.field_name === "capture_datetime" ||
        formValue.field_name === "host_datetime")
    ) {
      const hours = Math.round(
        DateTime.now().diff(
          DateTime.fromISO(dataTable[formValue?.field_name]),
          "hours",
        ).hours,
      );
      return hours > parseFloat(formValue.threshold) ? (
        <SeverityAlert severity={formValue.severity}>
          {hours} Hr(s)
        </SeverityAlert>
      ) : (
        <SeverityAlert>{hours} Hr(s)</SeverityAlert>
      );
    } else {
      return (
        <SeverityAlert severity={"ok"}>
          {dataTable[formValue.field_name]}{" "}
          {dataConfig?.find((label) => label.pgcolumn === formValue.field_name)
            ? dataConfig.find(
                (label) => label.pgcolumn === formValue.field_name,
              )?.unit
            : null}
        </SeverityAlert>
      );
    }
  };

  useEffect(() => {
    handleEnable();
  }, [formValue]);

  return (
    <AddAlertSC>
      <IconButton
        icon={<EditIcon />}
        style={{ padding: "5px" }}
        onClick={handleOpen}
      />
      <AlertModal
        title={`Edit Alert For: ${sme}`}
        open={open}
        onClose={handleClose}
        onSubmit={onSubmit}
        severity={formValue.severity}
        alertPreview={getAlertPreview()}
        submitDisabled={disable}
        size="xs"
      >
        {/* Trigger Description */}
        {getTriggerDescription() && (
          <PreviewHeader
            centered={false}
            style={{ paddingTop: "12px", paddingBottom: "12px" }}
          >
            {getTriggerDescription()}
          </PreviewHeader>
        )}

        {/* Notify Frequency */}
        {formValue.notify_frequency && (
          <PreviewHeader>
            <b>Notify Every {formValue.notify_frequency} hours</b>
          </PreviewHeader>
        )}

        {/* Current Value */}
        <div style={{ marginBottom: "16px" }}>
          Current Severity/Value: {getCurrentValue()}
        </div>

        {/* Form */}
        <Form
          fluid
          formValue={formValue}
          onChange={(formValue) => setFormValue(formValue)}
        >
          {!roleDisable ? (
            <>
              <Form.Group controlId="field_name">
                <Form.ControlLabel>Field Name</Form.ControlLabel>
                <Form.Control name="field_name" readOnly disabled block />
              </Form.Group>
              <Form.Group controlId="severity">
                <Form.ControlLabel>Severity</Form.ControlLabel>
                <Form.Control
                  name="severity"
                  accepter={SelectPicker}
                  data={severitySelector}
                  searchable={false}
                  block
                  disabled={roleDisable}
                />
              </Form.Group>
              <Form.Group controlId="operator">
                <Form.ControlLabel>Operator</Form.ControlLabel>
                <Form.Control
                  name="operator"
                  accepter={SelectPicker}
                  data={operatorSelector}
                  searchable={false}
                  block
                  disabled
                />
              </Form.Group>
              {formValue.field_name === "comp_vib_status" ? (
                <Form.Group controlId="threshold">
                  <Form.ControlLabel>Threshold</Form.ControlLabel>
                  <Form.Control
                    name="threshold"
                    checked={
                      formValue.threshold.toString() === "false" ? false : true
                    }
                    accepter={Toggle}
                    block
                    disabled={roleDisable}
                  />
                </Form.Group>
              ) : (
                <Form.Group controlId="threshold">
                  <Form.ControlLabel>Threshold</Form.ControlLabel>
                  <Form.Control
                    name="threshold"
                    accepter={InputNumber}
                    step={1}
                    block
                    postfix={
                      dataConfig?.find(
                        (label) => label.pgcolumn === formValue.field_name,
                      )
                        ? dataConfig.find(
                            (label) => label.pgcolumn === formValue.field_name,
                          )?.unit
                        : null
                    }
                    disabled={roleDisable}
                  />
                </Form.Group>
              )}
            </>
          ) : null}

          <Form.Group controlId="notify_frequency">
            <Form.ControlLabel>Notify Frequency</Form.ControlLabel>
            <Form.Control
              name="notify_frequency"
              accepter={InputNumber}
              step={1}
              postfix="hours"
              min={0}
              max={72}
              block
            />
          </Form.Group>
        </Form>
      </AlertModal>
    </AddAlertSC>
  );
};

const AddAlertSC = styled.div`
  display: inline-block;
  padding: 1%;
`;

const errMessage = (
  <Message showIcon type="error" header="Error">
    Something went wrong. Please try again.
  </Message>
);
